import { FormFieldsData, FormTemplate } from 'framework/form';
import {
  useNewProjectService,
  useProjectFinanceService,
  WASTE_STATUS,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import { useWasteService, WASTE_REPORTING_METHOD, WASTE_TYPE } from 'domain/shared/waste';
import { useParams } from 'react-router-dom';
import { useShowContext } from 'framework/show';
import {
  getRedmineIdentifierFromProject,
  ProjectDto,
  useProjectBootstrap,
  useRedmineIdentifier,
} from './WasteFormShared';
import { ShowSection } from 'framework/ui/layout';
import WasteForm from '../form/WasteForm';
import ChangeRequestActionButtons from 'domain/projects/newProject/finance/changeRequests/createupdate/ChangeRequestActionButtons';

const UpdateWasteLayout = () => {
  const { getById } = useNewProjectService();
  const { updateWaste, getWasteById, getWasteValidationSchema } = useProjectFinanceService();
  const { getProjects } = useWasteService();
  const { id, wasteId } = useParams();
  const { data } = useShowContext();
  const { getFinanceUrl } = useNewProjectUrl();

  const { project } = useProjectBootstrap({
    id,
    contextProject: data?.project as ProjectDto | undefined,
    fetchProjectById: getById as (id: string) => Promise<ProjectDto>,
  });
  const redmineIdentifierFromContext = useRedmineIdentifier(project);

  const getProjectId = async (employeeAlias: string, period: any, redmineIdentifier: string) => {
    const projects = await getProjects({
      id: employeeAlias,
      period,
    }).then((response: any) =>
      (response?.result || []).filter(
        (p: any) => (p.projectIdentifier ?? '').trim().toLowerCase() === redmineIdentifier.trim().toLowerCase(),
      ),
    );

    if (projects.length === 0) throw new Error(`No project matched Redmine identifier "${redmineIdentifier}".`);
    if (projects.length > 1) throw new Error(`Multiple projects matched Redmine identifier "${redmineIdentifier}".`);

    return projects[0].id;
  };

  const submitFormFunc = async (values: FormFieldsData) => {
    const projectData = data?.project || (await getById(id!));
    const redmineIdentifier = redmineIdentifierFromContext ?? getRedmineIdentifierFromProject(projectData);
    const waste = await getWasteById(wasteId!, {});

    const basePayload = {
      type: values.type,
      alias: waste?.alias || null,
      reason: values.reason,
      status: values.isDraft ? WASTE_STATUS.DRAFT : WASTE_STATUS.REGISTERED,
      davinciProjectId: id,
      budgetId: projectData?.defaultBudgetId || null,
    };

    if (values.type === WASTE_TYPE.OTHER_EXPENSES_WASTE) {
      return updateWaste(wasteId!, {
        ...basePayload,
        laborWasteDetails: null,
        otherWasteDetails: {
          cost: {
            amount: values.otherWasteDetails.cost?.amount,
            currency: values.otherWasteDetails.cost?.currency,
          },
          issueDate: values.otherWasteDetails.issueDate,
        },
      });
    }

    if (values.type === WASTE_TYPE.LABOR_COST_WASTE) {
      const { laborWasteDetails: details } = values;

      const reportingMethodHandlers = {
        [WASTE_REPORTING_METHOD.BY_EMPLOYEE_AND_TICKET_IDS]: () => ({
          reportingMethod: details.reportingMethod,
          employeeAlias: details.employeeAlias || null,
          ticketsIds: details.ticketsIds || [],
          projectId: null,
          period: null,
        }),

        [WASTE_REPORTING_METHOD.BY_PROJECT_PERIOD]: async () => {
          const projectId =
            details.projectId ?? (await getProjectId(details.employeeAlias, details.period, redmineIdentifier));

          return {
            reportingMethod: details.reportingMethod,
            employeeAlias: details.employeeAlias || null,
            ticketsIds: null,
            projectId,
            period: details.period
              ? {
                  startDate: details.period.startDate || '',
                  endDate: details.period.endDate || '',
                }
              : null,
          };
        },

        [WASTE_REPORTING_METHOD.BY_TICKET_IDS]: () => ({
          reportingMethod: details.reportingMethod,
          employeeAlias: null,
          ticketsIds: details.ticketsIds || [],
          redmineProjectIdentifier: details.redmineProjectIdentifier || redmineIdentifier,
          projectId: null,
          period: null,
        }),
      };

      const handler = reportingMethodHandlers[details.reportingMethod];

      if (!handler) {
        throw new Error(`Unknown reporting method: ${details.reportingMethod}`);
      }

      const laborWasteDetails = await handler();
      return updateWaste(wasteId!, {
        ...basePayload,
        laborWasteDetails,
        otherWasteDetails: null,
      });
    }

    throw new Error(`Unknown waste type: ${values.type}`);
  };

  return (
    <FormTemplate
      getDataFunc={() =>
        getWasteById(wasteId!, {}).then(input => ({
          ...input,
          laborWasteDetails: input?.laborWasteDetails && {
            ...input.laborWasteDetails,
            ticketsIds: input?.laborWasteDetails?.ticketsIds?.map((ticketId: StringOrNumber) => ticketId.toString()),
          },
        }))
      }
      submitFormFunc={submitFormFunc}
      getValidationSchemaFunc={getWasteValidationSchema}
      afterSubmit={{
        redirect: getFinanceUrl(id),
        message: 'You are awesome! The Waste has been successfully updated.',
      }}
    >
      <ShowSection title=" Update: Waste" titleVariant="h2" sx={{ p: 2 }}>
        <WasteForm title="" isProjectVisible={false} redmineIdentifier={redmineIdentifierFromContext} />
        <ChangeRequestActionButtons />
      </ShowSection>
    </FormTemplate>
  );
};
export default UpdateWasteLayout;
